/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { ArrowRightLeft, Rocket, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import {
  ChoiceCard,
  SelectedCheck,
  T9,
} from "@src/components/modules/WizardModule/WizardKit";
import IconTile from "@src/components/ui/IconTile";
import type { IconTileTone } from "@src/components/ui/IconTile/IconTile";

const Wrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 880px;
  margin-top: 8px;
`;
const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;
const Card = styled(ChoiceCard)`
  padding: 20px 24px;
  gap: 12px;
`;
const CardTitle = styled.div`
  font-size: 18px;
  line-height: 26px;
  font-weight: 600;
  color: ${T9.slate900};
`;
const CardText = styled.div`
  font-size: 14px;
  line-height: 21px;
  color: ${T9.slate600};
  p {
    margin: 0 0 8px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;
const CardFacts = styled.ul`
  margin: 0;
  padding: 12px 0 0 0;
  list-style: none;
  border-top: 1px solid ${T9.slate200};
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  line-height: 18px;
  color: ${T9.slate500};
  li strong {
    color: ${T9.slate700};
    font-weight: 600;
  }
`;
const WarningBox = styled.div<any>`
  display: flex;
  gap: 12px;
  width: 100%;
  background-color: var(--t9-warning-50);
  border: 1px solid var(--t9-accent-500);
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 24px;
  margin-bottom: 16px;
  box-sizing: border-box;
  color: var(--t9-info-700);
`;
const WarningTitle = styled.div<any>`
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 14px;
`;
const WarningText = styled.div<any>`
  font-size: 14px;
  line-height: 20px;
`;

type TypeOption = {
  id: "replica" | "migration";
  title: string;
  icon: LucideIcon;
  tone: IconTileTone;
  text: string[];
  facts: { label: string; value: string }[];
};
const OPTIONS: TypeOption[] = [
  {
    id: "migration",
    title: "SwiftIQ migration",
    icon: Rocket,
    tone: "brand",
    text: [
      "Copies a virtual machine’s data incrementally to disks in the target cloud with zero downtime, then moves it there for good. It can be synced any number of times, but deployed only once.",
    ],
    facts: [
      { label: "Syncs", value: "any number of times" },
      { label: "Deploys", value: "once, to move the VM" },
    ],
  },
  {
    id: "replica",
    title: "SwiftIQ replica",
    icon: ArrowRightLeft,
    tone: "info",
    text: [
      "Keeps an incremental copy of a virtual machine’s data on disks in the target cloud with zero downtime. It can be synced any number of times and deployed whenever disaster recovery is needed.",
    ],
    facts: [
      { label: "Syncs", value: "any number of times, on a schedule" },
      { label: "Deploys", value: "any time, for disaster recovery" },
    ],
  },
];

type Props = {
  selected: "replica" | "migration";
  onChange: (checked: boolean | null) => void;
};
@observer
class WizardType extends React.Component<Props> {
  select(id: "replica" | "migration") {
    if (id !== this.props.selected) {
      this.props.onChange(id === "replica");
    }
  }

  renderCard(option: TypeOption) {
    const selected = this.props.selected === option.id;
    return (
      <Card
        key={option.id}
        selected={selected}
        clickable
        role="radio"
        aria-checked={selected}
        tabIndex={0}
        data-test-id={`wizardType-${option.id}`}
        onClick={() => {
          this.select(option.id);
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.select(option.id);
          }
        }}
      >
        {selected ? <SelectedCheck /> : null}
        <IconTile icon={option.icon} size={56} tone={option.tone} />
        <CardTitle>{option.title}</CardTitle>
        <CardText>
          {option.text.map(t => (
            <p key={t}>{t}</p>
          ))}
        </CardText>
        <CardFacts>
          {option.facts.map(f => (
            <li key={f.label}>
              <strong>{f.label}:</strong> {f.value}
            </li>
          ))}
        </CardFacts>
      </Card>
    );
  }

  render() {
    return (
      <Wrapper>
        <Cards role="radiogroup" aria-label="Transfer type">
          {OPTIONS.map(o => this.renderCard(o))}
        </Cards>
        <WarningBox>
          <TriangleAlert size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <WarningTitle>Back up before you migrate</WarningTitle>
            <WarningText>
              Before you start, make sure there is a complete backup of the
              source VMs, including their data, applications and
              configuration.
            </WarningText>
          </div>
        </WarningBox>
      </Wrapper>
    );
  }
}

export default WizardType;
